/*
 * GEHC CONFIDENTIAL
 *
 * Copyright (c) 2025, 2025, GE HealthCare
 * All Rights Reserved
 *
 * This unpublished material is proprietary to GE HealthCare. The methods and
 * techniques described herein are considered trade secrets and/or
 * confidential. Reproduction or distribution, in whole or in part, is
 * forbidden except by express written permission of GE HealthCare.
 * GE is a trademark of General Electric Company used under trademark license.
 */

import { Injectable } from '@angular/core';
import { Logger, MessageService } from '@core/lib/services';
import { appConstants } from '@app/common/app-constants';
import { HealthCommand, HealthResponse, HealthStatus, HealthCheckType } from '@app/di/channel/window/healthcheck';
import { PostMessengerRegistryInfo, PostMessengerInfo, Stats } from '@app/di/channel/window/postmessage.types';
import { BackgroundService } from '@app/core/background-service';

const log = new Logger('PostMessengerService');

@Injectable({
  providedIn: 'root'
})
export class PostMessengerService {
  private readonly registry: Map<string, PostMessengerRegistryInfo> = new Map();

  private readonly HEALTH_TRACK_NORMAL_INTERVAL = appConstants.TIME.SS_TEN;
  private readonly HEALTH_TRACK_LAUNCH_INTERVAL = appConstants.TIME.SS_FIVE;
  private readonly WINDOW_TRACK_INTERVAL = appConstants.TIME.SS_TWO;
  private readonly HEALTH_TRACK_UNHEALTHY_MAX_TRY = appConstants.COUNT.FIVE;

  eventHandlerBound: (event: MessageEvent) => void;

  constructor(private readonly messageService: MessageService, private readonly backgroundService: BackgroundService) {
    try {
      this.eventHandlerBound = this._eventHandler.bind(this);
      window.addEventListener('message', this.eventHandlerBound);
    } catch (ex) {
      log.error('Failed to add post message listener!', ex);
    }
  }

  // START - Incoming messages
  private _eventHandler(event: MessageEvent) {
    let processed = false;
    const responseData = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
    const appId = responseData?.appId || responseData?.appID;
    if (appId) {
      const receiver: PostMessengerRegistryInfo = this.registry.get(appId.toUpperCase());
      if (receiver) {
        log.debug(`${receiver.targetAppId} : Incoming Message : Received`, responseData);
        if (this.updateStatsOnResponse(receiver, responseData)) {
          log.debug(`${receiver.targetAppId} : Message : Processed for stats`);
          processed = true;
        } else if (receiver.onResponse) {
          receiver.onResponse(responseData);
          log.debug(`${receiver.targetAppId} : Message : Handed over for processing`);
          processed = true;
        }
      }
    }
    // Log UnProcessed event
    !processed && log.warn('Incoming Message : Not processed', event);
  }

  private updateStatsOnResponse(regApp: PostMessengerRegistryInfo, data: HealthResponse): boolean {
    let processed = false;

    // Performance stats updates
    regApp.stats.performance.lastMessageReceivedOn = Date.now();
    if (regApp.onPerfUpdates) {
      this.relayPerfStat(regApp);
    }

    // Health stats updates - Amend if new type comes
    if (regApp.healthCheckType === HealthCheckType.PINGPONG && data.type === HealthCommand.PONG) {
      regApp.stats.health.isHealthy = data.status.toUpperCase() === HealthStatus.HEALTHY;
      regApp.stats.health.status = HealthStatus[data.status.toUpperCase()];
      regApp.stats.health.lastSessionActive = new Date(data.lastSessionActive);
      regApp.stats.health.noResponseAttempts = 0;
      this.messageService.broadcast('childApplication.alive', {
        appId: data.appId,
        lastSessionActive: regApp.stats.health.lastSessionActive
      });
      if (regApp.stats.health.isHealthy) {
        this.relayHealthStat(regApp);
      }
      processed = true;
    }
    return processed;
  }
  // END - Incoming messages

  // START - Outgoing messages
  sendMessage(targetAppId: string, msg) {
    const entry: PostMessengerRegistryInfo = this.registry.get(targetAppId.toUpperCase());
    if (entry) {
      entry.stats.performance.lastMessageSentOn = Date.now();
      entry.targetAppWindow.postMessage(msg, entry.targetOrigin || '*');
      log.debug(`${targetAppId} : Outgoing Message : Sent`, msg);
    } else {
      log.error(`${targetAppId} : Outgoing Message : App is not Registered`);
    }
  }

  private sendPingMessage(targetAppId: string) {
    const lastActiveTime = localStorage.getItem(`lastActiveTime.${appConstants.APP_ID.WFM}`);
    const pingMessage = {
      type: HealthCommand.PING,
      appId: appConstants.APP_ID.WFM,
      lastSessionActive: new Date(lastActiveTime)
    };

    log.info(`${targetAppId} : Health Check : Ping : Sending PING Request`);
    this.sendMessage(targetAppId, pingMessage);
  }
  // END - Outgoing messages

  // START - Stats
  private trackWindowStat(regApp: PostMessengerRegistryInfo) {
    if (regApp.onWindowUpdates) {
      try {
        regApp.closeTimer = this.backgroundService.setAsyncInterval(
          () => {
            regApp.stats.window.isClosed = regApp.targetAppWindow.closed;
            this.relayWindowStat(regApp);

            if (regApp.targetAppWindow.closed) {
              log.info(`${regApp.targetAppId} : Window : Close : WINDOW CLOSED`);
              this.unregister(regApp.targetAppId);
            }
          },
          this.WINDOW_TRACK_INTERVAL,
          { callerName: regApp.targetAppId + '-Window-Track' }
        );
        log.debug(`${regApp.targetAppId} : Window : Close : ENABLED : Interval - `, this.WINDOW_TRACK_INTERVAL);
      } catch (e) {
        regApp.closeTimer?.();
        log.error(`${regApp.targetAppId} : Window : Close : DISABLED`, e);
      }
    } else {
      log.warn(`${regApp.targetAppId} : Window : Close : DISABLED - No Handler`);
    }
  }

  private trackHealthStat(regApp: PostMessengerRegistryInfo) {
    // Track logic - Amend if new type comes
    if (regApp.onHealthUpdates) {
      if (regApp.healthCheckType === HealthCheckType.PINGPONG) {
        log.debug(
          `${regApp.targetAppId} : Health Check : Ping : LAUNCH MODE : Interval - `,
          this.HEALTH_TRACK_LAUNCH_INTERVAL
        );
        this.requestStatus(regApp.targetAppId, this.HEALTH_TRACK_LAUNCH_INTERVAL, true);
      }
    } else {
      log.warn(`${regApp.targetAppId} : Health Check : DISABLED - No Handler`);
    }
  }
  private requestStatus(targetAppId: string, timeout: number, isLaunch: boolean) {
    let attempt = appConstants.COUNT.ZERO;
    const regApp: PostMessengerRegistryInfo = this.registry.get(targetAppId);

    try {
      if (regApp) {
        const healthCheckInterval = regApp.healthCheckInterval ?? this.HEALTH_TRACK_NORMAL_INTERVAL;
        const healthCheckMaxRetry = regApp.healthCheckMaxRetry ?? this.HEALTH_TRACK_UNHEALTHY_MAX_TRY;
        regApp.closeInterval = this.backgroundService.setAsyncInterval(
          () => {
            if (regApp.targetAppWindow?.closed) {
              regApp.closeInterval?.('Window Closed');
              return;
            }

            // Keep the +2 for network latency so that No Response will not trigger viewer disconnected
            if (regApp.stats.health.noResponseAttempts >= healthCheckMaxRetry + 2) {
              regApp.closeInterval?.(regApp.targetAppId, ': No Response from Health check : Max Retry Exceeded');
              attempt = appConstants.COUNT.MINUS_ONE;
              regApp.stats.health = { ...regApp.stats.health, isHealthy: false, status: HealthStatus.UNHEALTHY };
              log.warn(`${regApp.targetAppId} : Health Check : Ping : NO RESPONSE - Timed Out`);
              this.relayHealthStat(regApp);
            }

            if (regApp.stats.health.isHealthy) {
              log.info(`${regApp.targetAppId} : Health Check : Ping : Healthy`);
              attempt = appConstants.COUNT.ZERO;
              if (isLaunch) {
                log.debug(
                  `${regApp.targetAppId} : Health Check : Ping : NORMAL MODE : Interval - `,
                  healthCheckInterval
                );
                regApp.closeInterval?.(
                  `${regApp.targetAppId} : Disabling Launch Mode, Switching to Normal Mode Health Check`
                );
                this.requestStatus(regApp.targetAppId, healthCheckInterval, false);
              }
            } else if (attempt >= appConstants.COUNT.ZERO && attempt < healthCheckMaxRetry) {
              log.debug(
                `${regApp.targetAppId} : Health Check : Ping : Unhealthy - Waiting ${
                  attempt + appConstants.COUNT.ONE
                }/${healthCheckMaxRetry}`
              );
              attempt = attempt + appConstants.COUNT.ONE;
            } else {
              attempt = appConstants.COUNT.MINUS_ONE;
              regApp.closeInterval?.(`${regApp.targetAppId} : Health Check : Ping : Unhealthy - Timed Out`);
              log.warn(`${regApp.targetAppId} : Health Check : Ping : Unhealthy - Timed Out`);
              this.relayHealthStat(regApp);
            }

            if (attempt > appConstants.COUNT.MINUS_ONE) {
              regApp.stats.health.noResponseAttempts = (regApp.stats.health.noResponseAttempts || 0) + 1;
              this.sendPingMessage(regApp.targetAppId);
            }
          },
          timeout,
          { callerName: regApp.targetAppId + '-Health-Track' }
        );
      } else {
        log.info(`${targetAppId} : Health Check : Ping : DISABLED - App already UnRegistered / not Registered`);
      }
    } catch (e) {
      regApp?.closeInterval?.('Unhandled Exception');
      log.error(`${regApp.targetAppId} : Health Check : Ping : DISABLED - Failed to monitor health status`, e);
    }
  }

  private relayHealthStat(regApp: PostMessengerRegistryInfo) {
    if (regApp.onHealthUpdates) {
      if (JSON.stringify(regApp.lastStatTracker.health) !== JSON.stringify(regApp.stats.health)) {
        log.debug(`${regApp.targetAppId} : Health Stat :`, regApp.stats.health);
        regApp.onHealthUpdates(regApp.stats.health);
      }
    } else {
      log.warn(`${regApp.targetAppId} : Health Stat : No callback set`);
    }
    regApp.lastStatTracker.health = { ...regApp.stats.health };
  }

  private relayWindowStat(regApp: PostMessengerRegistryInfo) {
    if (regApp.onWindowUpdates) {
      if (JSON.stringify(regApp.lastStatTracker.window) !== JSON.stringify(regApp.stats.window)) {
        log.debug(`${regApp.targetAppId} : Window Stat : `, regApp.stats.window);
        regApp.onWindowUpdates(regApp.stats.window);
      }
    } else {
      log.warn(`${regApp.targetAppId} : Window Stat : No callback set`);
    }
    regApp.lastStatTracker.window = { ...regApp.stats.window };
  }

  private relayPerfStat(regApp: PostMessengerRegistryInfo) {
    if (JSON.stringify(regApp.lastStatTracker.performance) !== JSON.stringify(regApp.stats.performance)) {
      log.debug(`${regApp.targetAppId} : Perf Stat : `, regApp.stats.performance);
      regApp.onPerfUpdates(regApp.stats.performance);
    }
    regApp.lastStatTracker.performance = { ...regApp.stats.performance };
  }
  // END - Stats

  // START - registration
  private isRegistrationDetailValid(regInfo: PostMessengerRegistryInfo): boolean {
    return !!(
      (regInfo.targetAppId && !regInfo.targetAppWindow && (regInfo.onResponse || regInfo.onPerfUpdates)) ||
      (regInfo.targetAppWindow && (regInfo.onWindowUpdates || regInfo.onHealthUpdates || regInfo.onResponse))
    );
  }

  unregister(targetAppId: string, closeWindow = false): boolean {
    const entry = this.registry.get(targetAppId.toUpperCase());
    if (entry) {
      // Clear Intervals
      entry.closeTimer?.(`${entry.targetAppId}: Unregistering`);
      entry.closeInterval?.(`${entry.targetAppId}: Unregistering`);
      log.debug(`${entry.targetAppId} : Registry : Subscripions cleaned`);

      // Close window
      if (closeWindow && !entry.targetAppWindow?.closed) {
        entry.targetAppWindow.close();
        log.debug(`${entry.targetAppId} : Registry : Window Closed`);
      }

      // Remove entry from registry
      this.registry.delete(targetAppId);
      log.info(`${targetAppId} : Registry : UnRegistered`);
      return true;
    } else {
      log.error(`${targetAppId} : Registry : App Not registered`);
    }
    return false;
  }

  register(regInfo: PostMessengerInfo) {
    // Validate and register
    if (this.isRegistrationDetailValid(regInfo)) {
      // Initialize the stats
      const stats = {
        window: { isClosed: false },
        performance: { lastMessageSentOn: null, lastMessageReceivedOn: null },
        health: { isHealthy: false, status: HealthStatus.UNHEALTHY, lastSessionActive: null }
      } as Stats;
      const lastStatTracker = {} as Stats;
      regInfo.targetAppId = regInfo.targetAppId.toUpperCase();
      const regApp: PostMessengerRegistryInfo = { ...regInfo, stats, lastStatTracker };

      this.registry.set(regApp.targetAppId, regApp);
      if (regApp.targetAppWindow) {
        try {
          // Tracker logics
          this.trackWindowStat(regApp);
          this.trackHealthStat(regApp);

          log.info(`${regApp.targetAppId} : Registry : Registered`);
          if (!regApp.onPerfUpdates) {
            log.warn(`${regApp.targetAppId} : Perf Stat : No callback set`);
          }
        } catch (e) {
          log.error(`${regApp.targetAppId} : Registry : Registration Failed`, e);
          this.unregister(regApp.targetAppId);
        }
      }
    } else {
      log.error(`${regInfo.targetAppId} : Registry : Invalid options provided`);
    }
  }
  // END - registration
}
